import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  UserPlus2,
  Users,
  Search,
  Check,
  X,
  MessageCircle,
  Loader2,
  UserX,
} from "lucide-react";
import { toast } from "sonner";

/**
 * FriendsCard
 *
 * Facebook-style friends module for customers:
 *   • Search other customers by (masked) name
 *   • Send/cancel friend requests
 *   • See incoming requests and accept/decline
 *   • Browse current friends, open DM, or unfriend
 */
function Avatar({ url, name, size = 36 }) {
  if (url) {
    return (
      <img
        src={url}
        alt={name || "avatar"}
        className="rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  const initials = (name || "?")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      className="rounded-full bg-[#B31E1E]/10 text-[#B31E1E] font-medium flex items-center justify-center"
      style={{ width: size, height: size, fontSize: size / 2.8 }}
    >
      {initials}
    </div>
  );
}

export default function FriendsCard() {
  const [tab, setTab] = useState("friends"); // friends | requests | search
  const [friends, setFriends] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyIds, setBusyIds] = useState({});

  // Search state
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);

  const markBusy = (id, v) =>
    setBusyIds((prev) => ({ ...prev, [id]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fr, rq] = await Promise.all([
        api.get("/friends/list"),
        api.get("/friends/requests"),
      ]);
      setFriends(fr.data?.friends || []);
      setIncoming(rq.data?.incoming || []);
      setOutgoing(rq.data?.outgoing || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Debounced search — either name (min 2 chars) or city (min 2 chars),
  // whichever the customer typed. Backend accepts either or both.
  useEffect(() => {
    const nameOk = q.trim().length >= 2;
    const cityOk = city.trim().length >= 2;
    if (!nameOk && !cityOk) {
      setResults([]);
      return;
    }
    const h = setTimeout(async () => {
      setSearching(true);
      try {
        const params = {};
        if (nameOk) params.q = q.trim();
        if (cityOk) params.city = city.trim();
        const { data } = await api.get("/friends/search", { params });
        setResults(data?.results || []);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(h);
  }, [q, city]);

  const sendRequest = async (u) => {
    markBusy(u.id, true);
    try {
      const { data } = await api.post("/friends/request", { to_id: u.id });
      if (data?.status === "accepted") {
        toast.success(`You and ${u.name} are now friends`);
        load();
      } else if (data?.status === "already_friends") {
        toast.info("Already friends");
        load();
      } else {
        toast.success(`Friend request sent to ${u.name}`);
        setResults((prev) => prev.filter((r) => r.id !== u.id));
        load();
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      markBusy(u.id, false);
    }
  };

  const respond = async (reqId, action, name) => {
    markBusy(reqId, true);
    try {
      await api.post("/friends/respond", { request_id: reqId, action });
      toast.success(
        action === "accept"
          ? `You are now friends with ${name}`
          : `Declined ${name}`
      );
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      markBusy(reqId, false);
    }
  };

  const cancelOutgoing = async (reqId, name) => {
    markBusy(reqId, true);
    try {
      await api.delete(`/friends/request/${reqId}`);
      toast.success(`Cancelled request to ${name}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      markBusy(reqId, false);
    }
  };

  const unfriend = async (u) => {
    if (!window.confirm(`Unfriend ${u.name}? Your DM history will be removed.`))
      return;
    markBusy(u.id, true);
    try {
      await api.delete(`/friends/${u.id}`);
      toast.success(`Unfriended ${u.name}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      markBusy(u.id, false);
    }
  };

  const startDm = async (u) => {
    markBusy(u.id, true);
    try {
      const { data } = await api.post("/threads/dm/open", { to_id: u.id });
      window.location.href = `/messages?thread=${data.thread.id}`;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      markBusy(u.id, false);
    }
  };

  return (
    <Card
      className="rounded-md border border-border p-6"
      data-testid="friends-card"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-full bg-[#B31E1E]/5 text-[#B31E1E] flex items-center justify-center shrink-0">
          <Users size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="font-display text-xl">Friends</h3>
            <Link
              to="/messages"
              className="text-sm text-[#B31E1E] hover:underline inline-flex items-center gap-1"
              data-testid="friends-open-inbox"
            >
              <MessageCircle size={14} /> Messages
            </Link>
          </div>
          <p className="text-sm text-gray-500 mt-0.5 max-w-2xl">
            Add other customers to your friends list, then chat privately.
            Businesses cannot see your friend connections.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-5 flex items-center gap-2 border-b border-border">
        {[
          { key: "friends", label: `My friends (${friends.length})` },
          {
            key: "requests",
            label: `Requests${incoming.length ? ` · ${incoming.length}` : ""}`,
          },
          { key: "search", label: "Find friends" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? "border-[#B31E1E] text-[#B31E1E] font-medium"
                : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
            data-testid={`friends-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        )}

        {!loading && tab === "friends" && (
          <>
            {friends.length === 0 && (
              <p className="text-sm text-gray-500" data-testid="friends-empty">
                You haven&apos;t added any friends yet. Use{" "}
                <button
                  onClick={() => setTab("search")}
                  className="text-[#B31E1E] hover:underline"
                >
                  Find friends
                </button>{" "}
                to get started.
              </p>
            )}
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {friends.map((u) => (
                <li
                  key={u.id}
                  className="flex items-center gap-3 border border-border rounded-sm px-3 py-2.5"
                  data-testid={`friend-row-${u.id}`}
                >
                  <Avatar url={u.avatar_data_url} name={u.name} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{u.name}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {[u.city, u.state].filter(Boolean).join(", ") || "—"}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-[35px] shrink-0"
                    disabled={busyIds[u.id]}
                    onClick={() => startDm(u)}
                    data-testid={`friend-message-${u.id}`}
                  >
                    <MessageCircle size={13} className="mr-1" /> Message
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="rounded-[35px] shrink-0 text-gray-400 hover:text-red-600"
                    disabled={busyIds[u.id]}
                    onClick={() => unfriend(u)}
                    data-testid={`friend-unfriend-${u.id}`}
                    title="Unfriend"
                  >
                    <UserX size={14} />
                  </Button>
                </li>
              ))}
            </ul>
          </>
        )}

        {!loading && tab === "requests" && (
          <div className="space-y-6">
            <div>
              <Badge variant="outline" className="rounded-sm mb-2">
                Incoming ({incoming.length})
              </Badge>
              {incoming.length === 0 && (
                <p className="text-sm text-gray-500">
                  No pending requests waiting on you.
                </p>
              )}
              <ul className="space-y-2">
                {incoming.map((r) => (
                  <li
                    key={r.id}
                    className="flex items-center gap-3 border border-border rounded-sm px-3 py-2.5"
                    data-testid={`friend-incoming-${r.id}`}
                  >
                    <Avatar url={r.user.avatar_data_url} name={r.user.name} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">
                        {r.user.name}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {[r.user.city, r.user.state].filter(Boolean).join(", ")}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="rounded-[35px] shrink-0 bg-[#B31E1E] hover:bg-[#8F1717]"
                      disabled={busyIds[r.id]}
                      onClick={() => respond(r.id, "accept", r.user.name)}
                      data-testid={`friend-accept-${r.id}`}
                    >
                      <Check size={13} className="mr-1" /> Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-[35px] shrink-0"
                      disabled={busyIds[r.id]}
                      onClick={() => respond(r.id, "decline", r.user.name)}
                      data-testid={`friend-decline-${r.id}`}
                    >
                      <X size={13} className="mr-1" /> Decline
                    </Button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <Badge variant="outline" className="rounded-sm mb-2">
                Sent ({outgoing.length})
              </Badge>
              {outgoing.length === 0 && (
                <p className="text-sm text-gray-500">
                  You haven&apos;t sent any pending requests.
                </p>
              )}
              <ul className="space-y-2">
                {outgoing.map((r) => (
                  <li
                    key={r.id}
                    className="flex items-center gap-3 border border-border rounded-sm px-3 py-2.5"
                    data-testid={`friend-outgoing-${r.id}`}
                  >
                    <Avatar url={r.user.avatar_data_url} name={r.user.name} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">
                        {r.user.name}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        Pending · {new Date(r.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-[35px] shrink-0"
                      disabled={busyIds[r.id]}
                      onClick={() => cancelOutgoing(r.id, r.user.name)}
                      data-testid={`friend-cancel-${r.id}`}
                    >
                      Cancel
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {!loading && tab === "search" && (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name (e.g. Alice Morgan)"
                  className="pl-8 rounded-sm"
                  data-testid="friends-search-input"
                />
              </div>
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <Input
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Or filter by city (e.g. Berkeley)"
                  className="pl-8 rounded-sm"
                  data-testid="friends-search-city-input"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1.5">
              Search by name, by city, or both. Full names are shown here so
              you can identify the right person before sending a request —
              your public profile still displays only your first initial.
            </p>

            <div className="mt-4">
              {searching && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 size={14} className="animate-spin" /> Searching…
                </div>
              )}
              {!searching &&
                (q.trim().length >= 2 || city.trim().length >= 2) &&
                results.length === 0 && (
                  <p className="text-sm text-gray-500">
                    No customers match — try a different spelling.
                  </p>
                )}
              <ul className="space-y-2">
                {results.map((u) => (
                  <li
                    key={u.id}
                    className="flex items-center gap-3 border border-border rounded-sm px-3 py-2.5"
                    data-testid={`friend-search-row-${u.id}`}
                  >
                    <Avatar url={u.avatar_data_url} name={u.name} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">
                        {u.name}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {[u.city, u.state].filter(Boolean).join(", ") || "—"}
                        {u.rating_count > 0 && (
                          <span className="ml-1.5 text-gray-400">
                            · {u.rating_avg?.toFixed(1)}★ ({u.rating_count})
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="rounded-[35px] shrink-0 bg-[#B31E1E] hover:bg-[#8F1717]"
                      disabled={busyIds[u.id]}
                      onClick={() => sendRequest(u)}
                      data-testid={`friend-add-${u.id}`}
                    >
                      <UserPlus2 size={13} className="mr-1" /> Add friend
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
